import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trash2, AlertTriangle, FileText, Mail, CheckCircle, XCircle } from "lucide-react";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";

interface UserInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: {
    id: string;
    name: string;
  };
  isBlock: boolean;
  isVerified: boolean;
  isSuperAdmin: boolean;
  media?: string;
}

export default function DeleteUserPage() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const hasDeletePermission = checkPermission("user_delete");

  useEffect(() => {
    if (!hasDeletePermission) {
      navigate("/employees");
    }
  }, [hasDeletePermission, navigate]);

  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-red-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-red-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700";

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        setError("");

        const userData = await ApiService.getUser(userId);
        setUser(userData);
      } catch (err: any) {
        setError(err.message || "Kullanıcı bilgileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  if (!hasDeletePermission) {
    return null;
  }

  const handleDelete = async () => {
    if (!userId || !user) return;
    if (confirmText !== `${user.firstName} ${user.lastName}`) {
      setError("Kullanıcı adını doğru yazmadınız!");
      return;
    }
    try {
      setDeleting(true);
      setError("");
      await ApiService.deleteUser(userId);
      navigate("/employees?tab=users");
    } catch (err: any) {
      setError(err.message || "Kullanıcı silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  const isConfirmCorrect = user && confirmText === `${user.firstName} ${user.lastName}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-red-600" />
          <span className="text-gray-600">Kullanıcı bilgileri yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Kullanıcı Bulunamadı</h1>
          <p className="mt-2 text-gray-600">İstenen kullanıcı bulunamadı veya silinmiş olabilir.</p>
          <button onClick={() => navigate("/employees")} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Üyeler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                onClick={() => navigate("/employees?tab=users")}
                className={`pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900`}>
                Kullanıcılar
              </button>
              <button
                onClick={() => navigate("/employees?tab=departments")}
                className={`pb-3 transition-all duration-200 text-gray-500 hover:text-gray-900`}>
                Departmanlar
              </button>
            </div>
          </div>
          </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Kullanıcıyı Sil</h1>
                <p className="text-sm text-gray-600 mt-1">Bu işlem geri alınamaz. Lütfen silme onayını dikkatli bir şekilde tamamlayın.</p>
                <div className="mt-3 h-1 w-20 rounded-full bg-red-200" />
              </div>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                Geri Dön
              </button>
            </div>

            {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">{error}</div>}

            <div className="space-y-5">
              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={`${sectionBadgeClassName} gap-1.5`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Dikkat
                  </span>
                </div>
                <p className="text-red-700 text-sm mb-3">
                  Kullanıcı silindiğinde aşağıdaki veriler kalıcı olarak kaldırılır:
                </p>
                <ul className="space-y-2 text-red-700 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    Kullanıcı bilgileri ve ayarları
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    Kullanıcı izinleri
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    İlişkili tüm veriler
                  </li>
                </ul>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={`${sectionBadgeClassName} gap-1.5`}>
                    <FileText className="w-3.5 h-3.5" />
                    Kullanıcı Bilgileri
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">Ad Soyad</label>
                    <p className="text-gray-900 font-medium">
                      {user.firstName} {user.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">E-posta</label>
                    <p className="text-gray-900 text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4" /> {user.email}
                    </p>
                  </div>
                  {user.department && (
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Departman</label>
                      <p className="text-gray-900 font-medium">{user.department.name}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">Hesap Durumu</label>
                    <p className="text-gray-900 font-medium">{user.isBlock ? "Engellenmiş" : "Aktif"}</p>
                  </div>
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">E-posta Doğrulama</label>
                    <p className="text-gray-900 font-medium">{user.isVerified ? "Doğrulandı" : "Bekliyor"}</p>
                  </div>
                  <div>
                    <label className="block text-gray-500 text-xs mb-1">Yetki Seviyesi</label>
                    <p className="text-gray-900 font-medium">{user.isSuperAdmin ? "Süper Admin" : "Standart"}</p>
                  </div>
                </div>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Silme Onayı
                  </span>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-800 text-sm">
                      <strong>Dikkat:</strong> Bu işlem geri alınamaz! Kullanıcı verilerinin tümü kalıcı olarak silinecektir.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Silme işlemini onaylamak için kullanıcının tam adını yazın:
                    </label>
                    <p className="text-sm text-gray-600 mb-2">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {user.firstName} {user.lastName}
                      </code>
                    </p>
                    <div className="relative">
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Kullanıcının tam adını yazın"
                        className={`${fieldClassName} ${
                          confirmText ? (isConfirmCorrect ? "border-green-400 bg-green-50" : "border-red-400 bg-red-50") : ""
                        }`}
                      />
                      {confirmText && (
                        <div className="absolute right-3 top-2.5">
                          {isConfirmCorrect ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                    İptal
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting || !isConfirmCorrect}
                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-md hover:from-red-700 hover:to-rose-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2">
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Siliniyor...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Kullanıcıyı Sil
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
